// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  AssetVehiclesChart,
  TagComposition,
  PerformanceTable,
} from '@advisor-app/features/dashboard';
import { useAppSelector } from '@advisor-app/store/store';
import { useLayoutEffect, type FC } from 'react';
import { PageContainer } from '@shared/ui-kit/components/page-container';
import {
  selectBroker,
  selectBrokerConnected,
  selectAccountType,
  selectAccount,
  selectSummary,
  selectAccountId,
} from '@advisor-app/store/slices/broker';
import { Broker } from '@shared/data-access/interfaces/api/brokerage/account';
import { portfolioApi } from '@advisor-app/store/api/brokerage/portfolio-api';
import { useAccounts } from '@advisor-app/features/accounts';
import { BrokerBalance } from '@advisor-app/features/dashboard/components/broker-balance';
import { BrokerBuyingPower } from '@advisor-app/features/dashboard/components/broker-buying-power';
import { BrokerMyInvestments } from '@advisor-app/features/dashboard/components/broker-my-investments';
import { useAppNavigate } from '@advisor-app/hooks';
import { useTranslation } from 'react-i18next';

export const BrokerDashboard: FC = () => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const broker = useAppSelector(selectBroker);
  const isBrokerConnected = useAppSelector(selectBrokerConnected);
  const accountType = useAppSelector(selectAccountType);
  const account = useAppSelector(selectAccount);
  const accountId = useAppSelector(selectAccountId);
  const brokerSummary = useAppSelector(selectSummary);

  useAccounts();

  const { data: performance } = portfolioApi.usePerformanceQuery(
    {
      params: {
        period: '3A',
        broker_key: broker.toLowerCase(),
        type_account: accountType,
      },
      payload: {
        account_id: account.accountId,
      },
    },
    { skip: !isBrokerConnected || !accountId },
  );

  useLayoutEffect(() => {
    if (!isBrokerConnected || !accountId) {
      navigate('/');
    }
  }, [accountId, isBrokerConnected, navigate]);

  const shouldRenderPerformanceTable = [Broker.IBKR].includes(broker as Broker);

  return (
    <PageContainer
      titlePage={t('common.dashboard', { ns: 'common' })}
      className="grid !gap-4"
    >
      {isBrokerConnected && (
        <>
          <BrokerBalance equity={brokerSummary.equity} />
          <BrokerBuyingPower
            buyingPower={brokerSummary.buyingPower}
            cash={brokerSummary.cash}
          />
          <BrokerMyInvestments />
          {shouldRenderPerformanceTable && (
            <PerformanceTable data={performance?.performance ?? []} />
          )}
          <div className="grid grid-cols-2 !gap-4">
            <AssetVehiclesChart />
            <TagComposition />
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default BrokerDashboard;
